import React from 'react';

function matches(event, spec) {
  if (typeof spec === 'string') {
    // Bare key shortcuts implicitly require no modifier keys — otherwise
    // `useKey('1')` would fire on ctrl+1 and collide with modifier-scoped
    // shortcuts.
    if (event.ctrlKey || event.metaKey || event.altKey) return false;
    return event.key.toLowerCase() === spec.toLowerCase();
  }
  if (spec && typeof spec === 'object') {
    if (spec.key && event.key.toLowerCase() !== spec.key.toLowerCase()) return false;
    if (spec.shift != null && event.shiftKey !== spec.shift) return false;
    if (spec.meta != null && event.metaKey !== spec.meta) return false;
    if (spec.ctrl != null && event.ctrlKey !== spec.ctrl) return false;
    if (spec.alt != null && event.altKey !== spec.alt) return false;
    // `mod: true/false` matches ctrl OR cmd — the platform-neutral way to
    // express "with modifier" for Mac + Windows/Linux together.
    if (spec.mod != null) {
      const hasMod = event.ctrlKey || event.metaKey;
      if (hasMod !== spec.mod) return false;
    }
    return true;
  }
  return false;
}

function isEditableTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export default function useKey(spec, handler, options = {}) {
  const handlerRef = React.useRef(handler);
  handlerRef.current = handler;

  const specKey = typeof spec === 'string' ? spec : JSON.stringify(spec);
  const allowInInputs = !!options.allowInInputs;
  const preventDefault = !!options.preventDefault;

  React.useEffect(() => {
    function onKeyDown(event) {
      if (!allowInInputs && isEditableTarget(event.target)) return;
      if (!matches(event, spec)) return;
      if (preventDefault) event.preventDefault();
      handlerRef.current(event);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, allowInInputs, preventDefault]);
}
